import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
  conversationTabPermissions,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const {
    meta: {
      permissions: routePermissions = [],
      conversationTab = false,
      customRoleGated = false,
    } = {},
  } = route;

  // `conversationTab`: a rota é uma aba de conversa liberável por Função
  // Personalizada. Sem isto, o papel-base ('agent') que o `AccountUser`
  // NATIVO injeta junto com a role deixaria entrar pela URL quem a sidebar
  // esconde — e o servidor devolveria uma lista vazia, sem explicar por quê.
  //
  // `customRoleGated` pede a MESMA coisa para uma superfície que não é aba de
  // conversa — a base de contatos, por exemplo. O nome antigo continua porque as
  // abas o usam desde a fase-60; o descarte do papel-base nunca teve nada de
  // específico de conversa.
  const permissions =
    conversationTab || customRoleGated
      ? conversationTabPermissions(userPermissions)
      : userPermissions;

  return hasPermissions(routePermissions, permissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, only the suspended screen is
  // reachable; administrators can also access billing to restore the account
  const userPermissions = getUserPermissions(user, to.params.accountId);
  const accessibleRoutes = userPermissions.includes('administrator')
    ? ['account_suspended', 'billing_settings_index']
    : ['account_suspended'];

  if (!accessibleRoutes.includes(to.name)) {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'conversation_awaiting_reply',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_awaiting_reply',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversation_through_awaiting_reply':
      return 'conversation_awaiting_reply';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';

// NooviChat: o back-end também notifica sobre cards do Pipeline
// (Notification::PRIMARY_ACTORS = %w[Conversation PipelineCard]), mas as telas
// de notificação foram escritas assumindo que o ator primário é SEMPRE uma
// conversa. Sem decidir o destino aqui, cada tela empurrava o id do CARD para
// uma rota de conversa — abrindo uma conversa inexistente.
export const PIPELINE_CARD_ACTOR_TYPE = 'PipelineCard';

export const isPipelineCardNotification = primaryActorType =>
  primaryActorType === PIPELINE_CARD_ACTOR_TYPE;

/**
 * Destino de uma notificação de card: o board do funil com o card aberto.
 *
 * `primaryActor` é o `push_event_data` do PipelineCard e chega em DUAS formas:
 * snake_case (getters `getNotifications` / `getFilteredNotifications`) e
 * camelCase (`getFilteredNotificationsV4`, que passa por camelcaseKeys) — daí
 * a leitura das duas chaves de `pipeline_id`.
 *
 * O payload não carrega o NOME do funil (e o store de pipelines só é populado
 * dentro do módulo Pipeline), mas a rota aceita o id puro como slug:
 * `:pipelineSlug(\d+[^/]*)` + `extractIdFromSlug` no PipelineView. Então dá
 * para abrir o card sem depender do nome.
 *
 * @returns {object|null} route location para `router.push`
 */
export const pipelineCardNotificationRoute = (accountId, primaryActor) => {
  if (!accountId) return null;

  const cardId = primaryActor?.id;
  const pipelineId = primaryActor?.pipelineId ?? primaryActor?.pipeline_id;

  // Card arquivado (SoftDeletable) chega sem payload utilizável: melhor cair no
  // índice do Pipeline do que engolir o clique como acontecia antes.
  if (!cardId || !pipelineId) {
    return { name: 'pipeline_index', params: { accountId: String(accountId) } };
  }

  return {
    name: 'pipeline_item_detail',
    params: {
      accountId: String(accountId),
      pipelineSlug: String(pipelineId),
      view: 'item',
      itemId: String(cardId),
    },
  };
};

export const isUpgradePageBypassRoute = routeName =>
  [
    'billing_settings_index',
    'settings_inbox_list',
    'general_settings_index',
    'agent_list',
  ].includes(routeName);
